import { useState, useEffect, useRef } from 'react';
import { messageAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import toast from 'react-hot-toast';
import './Chat.css';

export default function Chat() {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeout = useRef(null);

  useEffect(() => {
    messageAPI.getConversations()
      .then(({ data }) => setConversations(data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeConv) {
      socket?.emit('join_conversation', activeConv._id);
      fetchMessages(activeConv._id);
    }
  }, [activeConv?._id]);

  useEffect(() => {
    if (!socket) return;
    socket.on('new_message', (msg) => {
      setMessages(prev => [...prev, msg]);
      scrollToBottom();
    });
    socket.on('user_typing', (data) => setTyping(data.isTyping));
    return () => { socket.off('new_message'); socket.off('user_typing'); };
  }, [socket]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchMessages = async (convId) => {
    try {
      const { data } = await messageAPI.getMessages(convId);
      setMessages(data);
    } catch (_) {}
  };

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeConv) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('text', text.trim());
      const { data } = await messageAPI.sendMessage(activeConv._id, formData);
      setMessages(prev => [...prev, data]);
      setText('');
      socket?.emit('send_message', { ...data, conversationId: activeConv._id });
    } catch (_) { toast.error('Message send nahi hua'); }
    setSending(false);
  };

  const handleTyping = (e) => {
    setText(e.target.value);
    socket?.emit('typing', { conversationId: activeConv?._id, isTyping: true });
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket?.emit('typing', { conversationId: activeConv?._id, isTyping: false });
    }, 1500);
  };

  const getOtherParticipant = (conv) => conv.participants?.find(p => p._id !== user._id);

  return (
    <div className="chat-page page">
      <div className="chat-container">
        {/* Sidebar */}
        <div className="chat-sidebar">
          <div className="chat-sidebar-header"><h3>💬 Messages</h3></div>
          {loading ? <div className="spinner" /> : conversations.length === 0 ? (
            <div className="chat-empty">Koi conversation nahi</div>
          ) : conversations.map(conv => {
            const other = getOtherParticipant(conv);
            return (
              <div key={conv._id} className={`conv-item ${activeConv?._id === conv._id ? 'active' : ''}`}
                onClick={() => setActiveConv(conv)}>
                <div className="conv-avatar">{other?.name?.[0]?.toUpperCase() || '?'}</div>
                <div className="conv-info">
                  <div className="conv-name">{other?.name || 'User'}</div>
                  {conv.booking && <div className="conv-booking">{conv.booking.eventType} • {conv.booking.bookingNumber}</div>}
                  {conv.lastMessage && <div className="conv-last-msg">{conv.lastMessage}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Chat area */}
        <div className="chat-main">
          {!activeConv ? (
            <div className="chat-placeholder"><div style={{fontSize:'3rem'}}>💬</div><p>Conversation select karo</p></div>
          ) : (
            <>
              <div className="chat-header">
                <div className="chat-header-info">
                  <div className="conv-avatar">{getOtherParticipant(activeConv)?.name?.[0]?.toUpperCase()}</div>
                  <div>
                    <div className="chat-name">{getOtherParticipant(activeConv)?.name}</div>
                    {typing && <div className="typing-indicator">typing...</div>}
                  </div>
                </div>
              </div>

              <div className="messages-area">
                {messages.map(msg => (
                  <div key={msg._id} className={`message ${msg.sender._id === user._id ? 'mine' : 'theirs'}`}>
                    {msg.image && <img src={msg.image} alt="attachment" className="msg-image" />}
                    {msg.text && <div className="msg-text">{msg.text}</div>}
                    <div className="msg-time">{new Date(msg.createdAt).toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={sendMessage} className="chat-input-area">
                <input
                  type="text" value={text} onChange={handleTyping}
                  placeholder="Message likho..." className="chat-input"
                  disabled={sending}
                />
                <button type="submit" className="send-btn" disabled={!text.trim() || sending}>
                  {sending ? '...' : '➤'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
